import { FC } from 'react';

import { GridColumn, GridLayout, GridRow, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import { PageContentHeader } from 'components/common/PageContentHeader/PageContentHeader';
import { StickyActionBarWrapper } from 'components/common/StickyActionBarWrapper/StickyActionBarWrapper';
import { BiddingStrategySelect } from 'components/MultiCreationAuction/BiddingStrategySelect/BiddingStrategySelect';
import { BiddingStrategySelectBottomPanel } from 'components/MultiCreationAuction/BiddingStrategySelect/components/BiddingStrategySelectBottomPanel/BiddingStrategySelectBottomPanel';
import { BidsAndBudgetsTable } from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTable';
import { BidsAndBudgetsTableBottomPanel } from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableBottomPanel/BidsAndBudgetsTableBottomPanel';
import { SearchNotFound } from 'components/MultiCreationAuction/SearchNotFound/SearchNotFound';
import { VacanciesFiltersPanel } from 'components/MultiCreationAuction/VacanciesFiltersPanel/VacanciesFiltersPanel';
import { NotAvailableForCreationBanner } from 'components/MultiCreationAuction/VacanciesTable/NotAvailableForCreationBanner/NotAvailableForCreationBanner';
import { VacanciesTable } from 'components/MultiCreationAuction/VacanciesTable/VacanciesTable';
import { VacanciesTableBottomPanel } from 'components/MultiCreationAuction/VacanciesTable/VacanciesTableBottomPanel/VacanciesTableBottomPanel';

import { MultiCreationAuctionSteps } from 'store/models/multiCreation';
import { useSelector } from 'store/utils';

const TrlKeys = {
    vacanciesSelect: {
        title: 'MultiCreationAuction.vacanciesSelect.title',
        description: 'MultiCreationAuction.vacanciesSelect.description',
    },
    biddingStrategySelect: {
        title: 'MultiCreationAuction.BiddingStrategySelect.title',
    },
    bidsAndBudgets: {
        title: 'MultiCreationAuction.bidsAndBudgets.title',
        description: 'MultiCreationAuction.bidsAndBudgets.description',
    },
    breadcrumbs: {
        dashboard: 'MultiCreationAuction.breadcrumbs.dashboard',
        current: 'MultiCreationAuction.breadcrumbs.current',
    },
};

export const MultiCreationAuction: FC = () => {
    const vacancies = useSelector(({ multiCreation }) => multiCreation.vacancies);
    const activeStep = useSelector(({ multiCreation }) => multiCreation.activeStep);

    const trls = useTrls();
    const dashboardRoute = useDashboardRoute();

    const breadcrumbsConfig = [
        {
            to: dashboardRoute,
            children: trls(TrlKeys.breadcrumbs.dashboard),
        },
        { to: '#', children: trls(TrlKeys.breadcrumbs.current) },
    ];

    const STEPS = {
        [MultiCreationAuctionSteps.VacanciesSelectStep]: (
            <>
                <PageContentHeader
                    title={trls(TrlKeys.vacanciesSelect.title)}
                    description={trls(TrlKeys.vacanciesSelect.description)}
                    vSpacingBottom={24}
                    breadcrumbsConfig={breadcrumbsConfig}
                />
                <StickyActionBarWrapper
                    content={
                        <GridLayout>
                            <GridRow>
                                <GridColumn xs={4} s={8} m={12}>
                                    <VacanciesFiltersPanel />
                                    <VSpacing default={24} />
                                    <NotAvailableForCreationBanner />
                                    {vacancies.length ? <VacanciesTable /> : <SearchNotFound />}
                                </GridColumn>
                            </GridRow>
                        </GridLayout>
                    }
                    actionBar={<VacanciesTableBottomPanel />}
                />
            </>
        ),
        [MultiCreationAuctionSteps.BiddingStrategySelectStep]: (
            <>
                <PageContentHeader
                    title={trls(TrlKeys.biddingStrategySelect.title)}
                    vSpacingBottom={16}
                    breadcrumbsConfig={breadcrumbsConfig}
                />
                <StickyActionBarWrapper
                    content={<BiddingStrategySelect />}
                    actionBar={<BiddingStrategySelectBottomPanel />}
                />
            </>
        ),
        [MultiCreationAuctionSteps.BidsAndBudgetsStep]: (
            <>
                <PageContentHeader
                    title={trls(TrlKeys.bidsAndBudgets.title)}
                    description={trls(TrlKeys.bidsAndBudgets.description)}
                    vSpacingBottom={16}
                    breadcrumbsConfig={breadcrumbsConfig}
                />
                <StickyActionBarWrapper
                    content={<BidsAndBudgetsTable />}
                    actionBar={<BidsAndBudgetsTableBottomPanel />}
                />
            </>
        ),
    };

    return STEPS[activeStep];
};
